import { gql, useQuery } from "@apollo/client";
import LottieView from "lottie-react-native";
import { useState } from "react";
import { View } from "react-native";
import { ActivityIndicator, Text } from "react-native-paper";
import { AvatarProfile, ScreenContainer } from "../../components";
import Divider from "./Divider";
import OrderForm from "./OrderForm/index.web";
import { SHARED_ORDER_FIELDS, OrderStage, OrderType, PaymentMethod } from "./OrderForm/types";
import { ScreenProps } from "../types";
import { OrderNotAvailablePopOver } from ".";

interface OrderData extends OrderType {
  person: {
    firstName: string;
    lastName: string;
    profilePhoto?: string;
  };
  payable: boolean;
}

interface QueryType {
  proxy: {
    id: string;
    order: OrderData;
  };
}

export const QUERY = gql`
  query GetCommerceProxy($id: ID!) {
    proxy: commerceProxy(id: $id) {
      id
      order {
        ${SHARED_ORDER_FIELDS}
        payable
        person {
          id
          firstName
          lastName
          profilePhoto
        }
      }
    }
  }
`;

function ProxyPaymentScreen({ navigation, route }: ScreenProps<"DuesProxyPayment">) {
  const [orderIsPaid, setOrderIsPaid] = useState<boolean>();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();

  const { data } = useQuery<QueryType>(QUERY, {
    variables: { id: route.params.id },
    onCompleted: (data) => {
      const stage = data.proxy.order.stage;
      setOrderIsPaid(stage && [OrderStage.PAID, OrderStage.PENDING].includes(stage));
    },
  });

  const order = data?.proxy?.order;

  if (!order) {
    return (
      <View className="flex-1 bg-sky-white items-center justify-center">
        <ActivityIndicator />
      </View>
    );
  }

  if (!order.payable && !orderIsPaid) {
    return <OrderNotAvailablePopOver navigation={navigation} isProxy />;
  }

  return (
    <ScreenContainer testID="DuesScreen" navigationTitle={orderIsPaid ? "" : "Dues Summary"} noBackButton>
      {!orderIsPaid ? (
        <>
          <View className="px-6">
            <Text className="text-small-normal font-400 mt-4">
              You have been requested to pay PGA of America Membership Dues on behalf of {order.person.firstName}{" "}
              {order.person.lastName}.
            </Text>
            <Text className="text-large-normal font-700 mt-8">Dues Payment for:</Text>
            <View className="flex flex-row items-center my-4">
              <AvatarProfile
                profilePhoto={order.person.profilePhoto}
                firstName={order.person.firstName}
                lastName={order.person.lastName}
              />
              <Text className="text-regular-tight font-500 ml-4">
                {order.person.firstName} {order.person.lastName}
              </Text>
            </View>
            <Divider />
          </View>
          <OrderForm
            order={data.proxy.order}
            proxyId={data.proxy.id}
            onComplete={(paymentMethod) => {
              setOrderIsPaid(true);
              setPaymentMethod(paymentMethod);
            }}
          />
        </>
      ) : (
        <View className="items-center">
          <View className="w-28 h-28">
            <LottieView style={{ flex: 1 }} autoPlay source={require("../../../assets/lottie-icons/verified.json")} />
          </View>
          <Text className="text-title-2 font-700 text-ink-darkest mt-8 text-center">Payment Complete</Text>
          <Text className="text-regular-normal font-400 text-ink-dark my-6 px-4 text-center">
            {paymentMethod === PaymentMethod.Bank
              ? `Please allow up to 5 business days to process payments made by bank account. Payment confirmations will be sent to your email and to ${order.person.firstName} ${order.person.lastName} once the payment has been processed.`
              : `Your payment has been processed.\nPayment confirmations have been sent to your email and to ${order.person.firstName} ${order.person.lastName}.`}
          </Text>
        </View>
      )}
    </ScreenContainer>
  );
}

export default ProxyPaymentScreen;
